import {useState, useEffect, type Dispatch, type SetStateAction} from 'react';

import {styled} from '@mui/material/styles';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import {type SelectChangeEvent, useTheme} from '@mui/material';

import {useInputMenuContext} from 'context/InputMenu';
import useParseURL from 'hooks/useParseURL';

import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import CheckIcon from '@mui/icons-material/Check';

import {type CageResponse, type Site, type TransferAllocation} from 'client';
import {type IDictionary} from 'interfaces';

import useGetEmptyCages from 'hooks/useGetEmptyCages';
import useIsMounted from 'hooks/useIsMounted';
import useGetNonEmptyCages from 'hooks/plan/useGetNonEmptyCages';
import ProgressBar from 'components/Navigate/ProgressBar';
import {addTransfer, ONE_HUNDRED, updateTransfer} from './helpers/transfer';
import {useGetSiteCages, useGetSites, useGetTransfer} from 'hooks/swr';
import {mutateTransfer} from 'helpers/refreshHandler';
import GenericTextField from 'components/Generic/TextField';
import {GenericSelect, GenericSelectItem} from 'components/Generic/Selectors';
import {useTranslation} from 'react-i18next';
import {useSWRWithLoading} from 'hooks/swr/swrHookFactory';
import useGetLongTermRange from 'hooks/useGetLongTermRange';
import {useGetOverviewTableRange} from 'hooks/useGetOverviewTableRange';
import {refreshHandlerTransferDestiny} from 'components/OverviewTable/helpers';

export interface TransferTableProps {
  id?: string;
  siteId?: string;
  cageId?: string;
  date: Date | null;
}

export function TransferTable({id, siteId: propSiteID, cageId, date}: TransferTableProps) {
  const theme = useTheme();
  const {planId} = useParseURL();

  const inputMenuContext = useInputMenuContext();
  const inputMenuDispatch = inputMenuContext?.dispatch;
  const inputMenuState = inputMenuContext?.state;
  const [siteId, setSiteId] = useState(propSiteID);
  const [destinationSiteId, setDestinationSiteId] = useState<string>();
  const [originCageId, setOriginCageId] = useState<string | null | undefined>(cageId ?? '');
  const [destinationCagesPercentage, setDestinationCagesPercentage] = useState<IDictionary<number>>();
  const {t} = useTranslation('common');
  const {startDate, endDate} = useGetLongTermRange();
  const {startDate: _startDate, range, overviewTableResolution} = useGetOverviewTableRange();
  const isMounted = useIsMounted();
  const {data: sites} = useGetSites({resourceId: planId});

  const availableSites = sites;

  const isTransferUpdate = !!id;

  const {nonEmptyCages: originSiteCages, isLoading: isLoadingNonEmptyCages} = useGetNonEmptyCages({
    siteId,
    date,
    isMounted,
  });

  const {emptyCages: destinationEmptyCages, isLoading: isLoadingEmptyCages} = useGetEmptyCages({
    siteId: destinationSiteId,
    date,
    isMounted,
  });

  const {data: transfer, isLoading: isTransferLoading} = useSWRWithLoading(useGetTransfer({resourceId: id}));
  const {data: destinationSiteCages, isLoading: isAllCagesLoading} = useSWRWithLoading(
    useGetSiteCages({
      resourceId: destinationSiteId,
    }),
  );

  const destinationCages =
    destinationEmptyCages &&
    originSiteCages &&
    getDestinationCages(destinationEmptyCages, destinationSiteCages, transfer?.allocations);

  useEffect(() => {
    if (transfer) {
      setOriginCageId(transfer.fromCageId);
      setSiteId(transfer.fromSiteId);
      setDestinationSiteId(transfer.toSiteId);
      const perecntages = transfer.allocations.reduce<IDictionary<number>>((percentages, allocation) => {
        percentages[allocation.toCageId] = allocation.fraction * ONE_HUNDRED;
        return percentages;
      }, {});
      setDestinationCagesPercentage(perecntages);
    }
  }, [transfer]);

  const creationDisabled = !date || !originCageId || !destinationCagesPercentage;

  useEffect(() => {
    destinationEmptyCages &&
      destinationEmptyCages.forEach((cage) => {
        setDestinationCagesPercentage({...destinationCagesPercentage, [cage.id]: 0});
      });
  }, [destinationSiteId]);

  const communicationSuccess = (fromSite: string, toSite: string, cagesIds: string[]) => {
    startDate && endDate && mutateTransfer(planId, fromSite, toSite, cagesIds, startDate, endDate);

    if (_startDate && range && destinationSiteCages && destinationSiteId) {
      const _endDate = range[range.length - 1];
      const cageIds = destinationSiteCages.map((cage) => cage.id);

      refreshHandlerTransferDestiny({
        planId,
        cageIds,
        siteId: destinationSiteId,
        startDate: _startDate,
        endDate: _endDate,
        resolution: overviewTableResolution,
      });
    }
  };

  return (
    <>
      <TableContainer
        sx={{
          height: 400,
          minWidth: 500,
          borderBottom: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Table stickyHeader size="medium" sx={{borderCollapse: 'collapse'}}>
          <TableHead>
            <TableRow>
              <StyledTableCellBorderBottom>{t('from')}</StyledTableCellBorderBottom>
              <StyledTableCellBorderBottom>{t('to')}</StyledTableCellBorderBottom>
              <StyledTableCellBorderBottom align="right">{t('allocation')}</StyledTableCellBorderBottom>
            </TableRow>
          </TableHead>
          <TableBody>
            {availableSites && (
              <DestinySiteRow
                originSiteId={siteId}
                availableSites={availableSites}
                destinationSiteId={destinationSiteId}
                setDestinationSiteId={setDestinationSiteId}
                setOriginSiteId={setSiteId}
                disabled={isTransferUpdate}
              />
            )}

            {originSiteCages && (
              <OriginCageRow
                originSiteCages={originSiteCages}
                originCageId={originCageId}
                setOriginCageId={setOriginCageId}
                destinationCages={destinationCagesPercentage}
                disabled={isTransferUpdate}
              />
            )}
            {destinationCages?.map((cage) => (
              <DestinCageRow
                key={cage.id}
                cage={cage}
                destinationCages={destinationCagesPercentage}
                setDestinationCage={setDestinationCagesPercentage}
              />
            ))}
            {destinationCages?.length === 0 && <NoDestinationCagesRow />}
          </TableBody>
        </Table>
      </TableContainer>
      <Box display="flex" flexDirection="row-reverse" p={1} bgcolor="background.paper">
        <Box>
          <Button
            data-testid="button-add-update-transfer"
            disabled={creationDisabled}
            variant="contained"
            color="primary"
            endIcon={<CheckIcon />}
            onClick={() => {
              if (siteId && !isTransferUpdate && destinationSiteId) {
                addTransfer({
                  fromSiteId: siteId,
                  toSiteId: destinationSiteId,
                  transferDate: date,
                  destinationCagesPercentage,
                  originCageId,
                  inputMenuDispatch,
                  inputMenuState,
                  communicationSuccess,
                  t,
                  isMounted,
                });
              }
              if (id && transfer) {
                updateTransfer({
                  transferId: id,
                  transferDate: date,
                  destinationCagesPercentage,
                  inputMenuDispatch,
                  inputMenuState,
                  communicationSuccess,
                  t,
                  originCageId: transfer.fromCageId,
                  fromSiteId: transfer.fromSiteId,
                  toSiteId: transfer.toSiteId,
                  isMounted,
                });
              }
            }}
          >
            {id ? t('update') : t('add')}
          </Button>
        </Box>
      </Box>
      {(isLoadingNonEmptyCages ||
        isLoadingEmptyCages ||
        (!!id && isTransferLoading) ||
        (!!destinationSiteId && isAllCagesLoading)) && <ProgressBar barWidth="100%" />}
    </>
  );
}

const DestinySiteRow = ({
  originSiteId,
  availableSites,
  destinationSiteId,
  setDestinationSiteId,
  setOriginSiteId,
  disabled,
}: {
  originSiteId?: string;
  availableSites: Site[];
  destinationSiteId?: string;
  setDestinationSiteId: Dispatch<SetStateAction<string | undefined>>;
  setOriginSiteId: Dispatch<SetStateAction<string | undefined>>;
  disabled: boolean;
}) => {
  const theme = useTheme();
  const {t} = useTranslation('common');

  return (
    <TableRow sx={{borderBottom: `1px solid ${theme.palette.divider}`}}>
      <SelectCell>
        <GenericSelect
          value={originSiteId ?? ''}
          onChange={({target: {value}}) => {
            setOriginSiteId(value as string);
          }}
          data-testid="origin-site-select"
          label={t('selectSite')}
          disabled={disabled}
        >
          {availableSites.map((site) => (
            <GenericSelectItem value={site.id} key={site.id} data-testid={`origin-${site.name}`}>
              {site.name}
            </GenericSelectItem>
          ))}
        </GenericSelect>
      </SelectCell>
      <SelectCell>
        <GenericSelect
          value={destinationSiteId ?? ''}
          onChange={({target: {value}}) => {
            setDestinationSiteId(value as string);
          }}
          data-testid="destiny-site-select"
          label={t('selectSite')}
          disabled={disabled}
        >
          {availableSites.map((site) => (
            <GenericSelectItem value={site.id} key={site.id} data-testid={`destiny-${site.name}`}>
              {site.name}
            </GenericSelectItem>
          ))}
        </GenericSelect>
      </SelectCell>
    </TableRow>
  );
};

const OriginCageRow = ({
  originSiteCages,
  originCageId,
  setOriginCageId,
  destinationCages,
  disabled,
}: {
  originSiteCages: CageResponse[];
  originCageId: string | null | undefined;
  setOriginCageId: Dispatch<SetStateAction<string | null | undefined>>;
  destinationCages: IDictionary<number> | undefined;
  disabled: boolean;
}) => {
  const {t} = useTranslation('common');

  const theme = useTheme();
  const handleChange = (event: SelectChangeEvent<unknown>) => {
    setOriginCageId(event.target.value as string);
  };

  const destinationCageIds = destinationCages && Object.keys(destinationCages);
  const totalTransfarePercentage =
    destinationCages && destinationCageIds?.map((cageId) => destinationCages[cageId]).reduce((a, b) => a + b, 0);
  const availability = originCageId ? ONE_HUNDRED : 0;
  const totalAvailability = availability - (totalTransfarePercentage ?? 0);

  return (
    <TableRow>
      <StyledTableCellBorderBottomRight
        sx={{
          padding: theme.spacing(1),
          paddingLeft: theme.spacing(2),
          minWidth: theme.spacing(30),
        }}
      >
        <GenericSelect
          value={originCageId}
          onChange={handleChange}
          data-testid="origin-cage-select"
          disabled={disabled}
          label={t('selectCage')}
        >
          {originSiteCages.map((cage) => (
            <GenericSelectItem value={cage.id} key={cage.id} data-testid={`origin-${cage.name}`}>
              {cage.name}
            </GenericSelectItem>
          ))}
        </GenericSelect>
      </StyledTableCellBorderBottomRight>
      <StyledTableCellBorderBottomRight />
      <StyledTableCellBorderBottom align="right">{`${totalAvailability} %`}</StyledTableCellBorderBottom>
    </TableRow>
  );
};

const DestinCageRow = ({
  cage,
  destinationCages,
  setDestinationCage,
}: {
  cage: CageResponse;
  destinationCages: IDictionary<number> | undefined;
  setDestinationCage: Dispatch<SetStateAction<IDictionary<number> | undefined>>;
}) => {
  return (
    <TableRow>
      <StyledTableCellBorderBottomRight component="th" scope="row"></StyledTableCellBorderBottomRight>
      <StyledTableCellBorderBottomRight>{cage.name}</StyledTableCellBorderBottomRight>
      <StyledTableCellBorderBottom align="right">
        <GenericTextField
          variant="standard"
          inputProps={{'data-testid': `allocation-${cage.name}`}}
          InputProps={{disableUnderline: false, endAdornment: <div>%</div>, style: {textAlign: 'right'}}}
          value={(destinationCages && destinationCages[cage.id]) ?? 0}
          onChange={(event) => {
            const percentage = parseFloat(event.target.value);
            setDestinationCage({...destinationCages, [cage.id]: isNaN(percentage) ? 0 : percentage});
          }}
          style={{width: 80}}
        />
      </StyledTableCellBorderBottom>
    </TableRow>
  );
};

const NoDestinationCagesRow = () => {
  const {t} = useTranslation('common');
  return (
    <TableRow>
      <StyledTableCellBorderBottomRight component="th" scope="row"></StyledTableCellBorderBottomRight>
      <StyledTableCellBorderBottomRight colSpan={2}>{t('no_trnasfer_cages')}</StyledTableCellBorderBottomRight>
    </TableRow>
  );
};

const SelectCell = styled(TableCell)(({theme}) => ({
  padding: theme.spacing(1),
  paddingLeft: theme.spacing(2),
  minWidth: theme.spacing(30),
  border: 'none',
}));

const StyledTableCellBorderBottomRight = styled(TableCell)(({theme}) => ({
  borderBottom: `1px solid ${theme.palette.divider}`,
  borderRight: `1px solid ${theme.palette.divider}`,
}));

const StyledTableCellBorderBottom = styled(TableCell)(({theme}) => ({
  borderBottom: `1px solid ${theme.palette.divider}`,
}));

const getDestinationCages = (
  emptyCages: CageResponse[],
  allCages?: CageResponse[],
  allocations?: TransferAllocation[],
): CageResponse[] => {
  if (!allCages || !allocations) {
    return emptyCages;
  }
  const allocatedCagesSet = new Set(allocations.map((allocation) => allocation.toCageId));
  const allocatedCages = allCages.filter((cage) => allocatedCagesSet.has(cage.id));
  return [...allocatedCages, ...emptyCages];
};
